/**
 * Roster presentation model.
 *
 * The UI never owns fighter data. Everything shown on the character-select and
 * HUD — names, archetypes, taglines, resource meter names, mechanic copy,
 * difficulty — is *derived* from `src/combat`'s roster stats so there is exactly
 * one source of truth (ARCHITECTURE.md §4.1 corollary, §16).
 *
 * Resolution order, most authoritative first:
 *   1. `ctx.data.fighters` — the registry `src/combat` publishes during init.
 *   2. `src/combat/index.js` — the subsystem's public surface (dynamic import so
 *      `src/ui` stays loadable in isolation before combat lands, and so a missing
 *      export degrades to a neutral UI instead of a boot failure).
 *   3. `PROVISIONAL_ROSTER` — a minimal bootstrap table used only when neither of
 *      the above is available. It exists so the menus are navigable and testable
 *      standalone; it is superseded automatically the moment combat is present.
 *
 * Nothing in this module runs per frame.
 */

/**
 * Bootstrap-only fighter table. Values mirror ARCHITECTURE.md §16.
 * DELETE once `src/combat/index.js` is guaranteed to export the roster — the
 * resolver already prefers the real data whenever it exists.
 */
const PROVISIONAL_ROSTER = [
  {
    id: 'kestrel',
    prefix: 'ky',
    name: 'Kestrel',
    archetype: 'counter-striker',
    tagline: 'Reads you, then removes you.',
    discipline: 'Vel Sarra',
    maxHealth: 950,
    height: 1.72,
    mass: 62,
    walkFwdSpeed: 2.05,
    dashSpeed: 4.6,
    dashFrames: 13,
    backdashSpeed: 5.0,
    pushboxRadius: 0.3,
    guardMax: 100,
    meterName: 'Read',
    meterMax: 100,
    meterOnCounterHit: 18,
    meterOnParry: 25,
    meterOnDamageTaken: 0.04,
    stances: ['', 'flow'],
    parryWindowFrames: 5,
    parryAdvantageFrames: 12,
    anchorArmorHits: 0,
    anchorArmorDamageMul: 0,
  },
  {
    id: 'bulwark',
    prefix: 'bw',
    name: 'Bulwark',
    archetype: 'power-grappler',
    tagline: 'There is no outside.',
    discipline: 'Hold Iron',
    maxHealth: 1100,
    height: 1.98,
    mass: 128,
    walkFwdSpeed: 1.35,
    dashSpeed: 3.2,
    dashFrames: 19,
    backdashSpeed: 3.0,
    pushboxRadius: 0.38,
    guardMax: 130,
    meterName: 'Grit',
    meterMax: 100,
    meterOnCounterHit: 6,
    meterOnParry: 0,
    meterOnDamageTaken: 0.16,
    stances: ['', 'anchor'],
    parryWindowFrames: 0,
    parryAdvantageFrames: 0,
    anchorArmorHits: 1,
    anchorArmorDamageMul: 0.45,
  },
];

/** Difficulty words, indexed 1..5. */
const DIFFICULTY_WORDS = ['', 'ENTRY', 'STEADY', 'MODERATE', 'TECHNICAL', 'DEMANDING'];

/**
 * Normalises whatever shape the roster arrives in into a plain array of stats.
 * Accepts an array, a `{ id: stats }` map, or `{ ROSTER, ROSTER_IDS }`.
 * @param {unknown} source
 * @returns {object[]}
 */
function toStatsArray(source) {
  if (!source) return [];
  if (Array.isArray(source)) return source.filter((s) => s && typeof s.id === 'string');
  if (typeof source !== 'object') return [];
  const obj = /** @type {Record<string, any>} */ (source);
  if (Array.isArray(obj.ROSTER_IDS) && obj.ROSTER) {
    const out = [];
    for (const id of obj.ROSTER_IDS) {
      const s = obj.ROSTER[id];
      if (s) out.push(s);
    }
    if (out.length) return out;
  }
  if (obj.ROSTER) return toStatsArray(obj.ROSTER);
  const out = [];
  for (const key in obj) {
    const s = obj[key];
    if (s && typeof s === 'object' && typeof s.name === 'string' && typeof s.maxHealth === 'number') {
      out.push(s);
    }
  }
  return out;
}

/**
 * Resolves the raw roster stats for this session.
 * @param {object} ctx
 * @returns {Promise<{stats: object[], source: string}>}
 */
export async function resolveRosterStats(ctx) {
  const fromCtx = toStatsArray(ctx && ctx.data ? ctx.data.fighters : null);
  if (fromCtx.length) return { stats: fromCtx, source: 'ctx.data.fighters' };

  try {
    const mod = await import('../../combat/index.js');
    const fromModule = toStatsArray(mod);
    if (fromModule.length) return { stats: fromModule, source: 'src/combat/index.js' };
  } catch {
    // src/combat has not published an index yet; fall through.
  }

  if (ctx && ctx.log) {
    ctx.log.warn('[ui] roster unavailable from src/combat — using the provisional UI table');
  }
  return { stats: PROVISIONAL_ROSTER, source: 'provisional' };
}

/**
 * Execution difficulty, derived from gameplay attributes rather than authored in
 * the UI. Frame-tight defensive windows, stance management, movement-heavy
 * neutral and a smaller health pool all raise the load.
 *
 * Kestrel resolves to 5 (5f parry, stance, fastest walk, 13f dash, 950 HP);
 * Bulwark resolves to 2 (stance only). If the roster later carries an explicit
 * `difficulty` field this function defers to it.
 *
 * @param {object} s fighter stats
 * @returns {number} 1..5
 */
export function difficultyOf(s) {
  if (typeof s.difficulty === 'number') {
    return Math.max(1, Math.min(5, Math.round(s.difficulty)));
  }
  let load = 1;
  const parry = s.parryWindowFrames || 0;
  if (parry > 0) load += parry <= 6 ? 2 : 1;
  const stanceCount = Array.isArray(s.stances) ? s.stances.filter(Boolean).length : 0;
  load += stanceCount;
  if ((s.walkFwdSpeed || 0) >= 1.8) load += 1;
  if ((s.dashFrames || 99) <= 15) load += 1;
  if ((s.maxHealth || 1000) <= 1000) load += 1;
  return Math.max(2, Math.min(5, load));
}

/**
 * Short lines describing the fighter's distinguishing mechanic, assembled from
 * roster fields. No fighter name or mechanic name is hard-coded here.
 * @param {object} s
 * @returns {string[]}
 */
export function mechanicLines(s) {
  const lines = [];
  const stance = Array.isArray(s.stances) ? s.stances.find(Boolean) : '';
  if (stance) {
    const enter = s.stanceEnterFrames ? ` — ${s.stanceEnterFrames}f to enter` : '';
    lines.push(`${stance.toUpperCase()} STANCE${enter}`);
  }

  const meter = s.meterName || 'METER';
  const onHit = (s.meterOnCounterHit || 0) + (s.meterOnParry || 0);
  const onTaken = (s.meterOnDamageTaken || 0) * 100;
  let how;
  if (onTaken > onHit) how = 'builds while absorbing punishment';
  else if ((s.meterOnParry || 0) > 0) how = 'builds on counter-hits and parries';
  else how = 'builds on counter-hits and whiff punishes';
  lines.push(`${meter.toUpperCase()} — ${how}, caps at ${s.meterMax || 100}`);

  if ((s.parryWindowFrames || 0) > 0) {
    lines.push(
      `${s.parryWindowFrames}f parry window, +${s.parryAdvantageFrames || 0}f advantage on success`,
    );
  }
  if ((s.anchorArmorHits || 0) > 0) {
    const reduction = Math.round((1 - (s.anchorArmorDamageMul || 0)) * 100);
    lines.push(`${s.anchorArmorHits} armour hit while anchored, ${reduction}% damage absorbed`);
  }
  if ((s.anchorThrowDamageMul || 0) > 1) {
    lines.push(`Throw chains hit ${Math.round((s.anchorThrowDamageMul - 1) * 100)}% harder anchored`);
  }
  return lines;
}

/**
 * Builds the full presentation model for the roster, including relative stat
 * bars normalised across the whole roster so the comparison is meaningful.
 * @param {object[]} statsList
 * @returns {object[]} fighter view models
 */
export function buildRosterView(statsList) {
  const maxOf = (key, floor) => {
    let m = floor;
    for (const s of statsList) if ((s[key] || 0) > m) m = s[key] || 0;
    return m || 1;
  };
  const maxHealth = maxOf('maxHealth', 1);
  const maxWalk = maxOf('walkFwdSpeed', 1);
  const maxDash = maxOf('dashSpeed', 1);
  const maxMass = maxOf('mass', 1);
  const maxGuard = maxOf('guardMax', 1);

  return statsList.map((s, index) => {
    const difficulty = difficultyOf(s);
    const stance = Array.isArray(s.stances) ? s.stances.find(Boolean) || '' : '';
    return {
      index,
      id: s.id,
      prefix: s.prefix || '',
      name: s.name || s.id,
      archetype: (s.archetype || 'fighter').replace(/-/g, ' '),
      tagline: s.tagline || '',
      discipline: s.discipline || '',
      meterName: s.meterName || 'METER',
      meterMax: s.meterMax || 100,
      stance,
      difficulty,
      difficultyWord: DIFFICULTY_WORDS[difficulty] || 'MODERATE',
      mechanic: mechanicLines(s),
      /** Height and pushbox drive the select-screen silhouette proportions. */
      height: s.height || 1.8,
      pushboxRadius: s.pushboxRadius || 0.34,
      hasParry: (s.parryWindowFrames || 0) > 0,
      hasArmor: (s.anchorArmorHits || 0) > 0,
      bars: [
        { name: 'Vitality', value: s.maxHealth || 0, frac: (s.maxHealth || 0) / maxHealth, unit: '' },
        { name: 'Walk', value: s.walkFwdSpeed || 0, frac: (s.walkFwdSpeed || 0) / maxWalk, unit: ' m/s' },
        { name: 'Dash', value: s.dashSpeed || 0, frac: (s.dashSpeed || 0) / maxDash, unit: ' m/s' },
        { name: 'Mass', value: s.mass || 0, frac: (s.mass || 0) / maxMass, unit: ' kg' },
        { name: 'Guard', value: s.guardMax || 0, frac: (s.guardMax || 0) / maxGuard, unit: '' },
      ],
      /** The raw stats record, for anything that needs a field not modelled here. */
      stats: s,
    };
  });
}

/**
 * Resolves and models the roster in one call.
 * @param {object} ctx
 * @returns {Promise<{fighters: object[], source: string}>}
 */
export async function loadRosterView(ctx) {
  const { stats, source } = await resolveRosterStats(ctx);
  return { fighters: buildRosterView(stats), source };
}

/**
 * Finds a fighter view model by id, falling back to the first entry so the UI
 * can never end up with an undefined fighter.
 * @param {object[]} fighters
 * @param {string} id
 */
export function findFighter(fighters, id) {
  for (const f of fighters) if (f.id === id) return f;
  return fighters[0] || null;
}
